import { createContext, useEffect, useRef, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/useToast";
import { publicApi, LoginCredentials } from "@/services/publicApi";
import {
  isAuthenticated,
  getAccessToken,
  setSessionToken,
  clearSessionToken,
  clearAllAuth,
  clearLegacyTokenStorage,
  broadcastLogout,
  AUTH_LOGOUT_CHANNEL,
  LogoutBroadcastMessage,
} from "@/utils/auth";
import { takeReturnPath } from "@/utils/postLoginRedirect";
import { api } from "@/services/api";
import { identifyUser, logoutUser } from "@/utils/amplitude";
import { userLoggedIn, userLoggedOut } from "@/utils/tracking";
import { getRedirectRoute, OnboardingStep } from "@/utils/onboardingConfig";
import * as amplitude from "@amplitude/analytics-browser";

import { LOGIN_FAILED, LOGGED_OUT } from "@/utils/toastMessages";
interface User {
  id: string;
  email: string;
  fullName: string;
  mobileNumber: string;
  role: string;
  roles: string[];
  onboardingStep?: string | null;
  wrapupStep?: number | null;
  buildPabStep?: number | null;
  buildExpertStep?: number | null;
  createdAt?: string | null;
  codeOfConductAccepted?: boolean | null;
  /** Which role's workspace the user is currently viewing the app as (dual-role support). */
  activeWorkspace?: string | null;
  /** May start the Expert application: allowed by an admin, or already an Expert. */
  canApplyAsExpert: boolean;
  /** "manual" (email magic link) or "linkedin". */
  authProvider?: string | null;
  /** Company part of the Group / Company Code. */
  organizationCode?: string | null;
  /** Team part of the Group / Company Code, if one was given. */
  teamCode?: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  verifyEmail: (token: string) => Promise<void>;
  sendLoginLink: (email: string) => Promise<void>;
  verifyLoginToken: (token: string) => Promise<{ route: string }>;
  // Review-account-only password login (backend 403s every other email).
  passwordLogin: (email: string, password: string) => Promise<{ route: string }>;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
  impersonate: (email: string) => Promise<void>;
  getImpersonateLoginUrl: (
    email: string,
    secretKey?: string,
  ) => Promise<{ loginUrl: string; email: string }>;
  // DISABLED (multi-account): the account switcher's surface.
  // accounts: AccountSlot[];
  // activeAccountIndex: number;
  // logoutAll: () => void;
  // refreshConnectedAccounts: () => Promise<void>;
  // switchAccount: (userId: string) => Promise<void>;
  // addAccount: (expectedTargetUserId?: string) => void;
  // removeAccount: (userId: string) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [authStatus, setAuthStatus] = useState<boolean>(false);
  const navigate = useNavigate();
  const { showToast } = useToast();
  /**
   * Bumped every time an explicit sign-in establishes a session as a specific
   * user (login, magic-link verify, signup verify). A silent token refresh
   * deliberately does NOT bump it — the identity is unchanged there.
   *
   * checkAuth uses it to tell "someone else took this session over mid-flight"
   * apart from "my own token was refreshed mid-flight".
   */
  const sessionEpochRef = useRef(0);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Fetch /users/me with a specific token (bypasses the active-session interceptor). */
  const fetchUserWithToken = async (token: string): Promise<User> => {
    const d = await api.users.getCurrentUser(token);
    return {
      id: d.id,
      email: d.email,
      fullName: d.fullName,
      mobileNumber: (d as unknown as { mobileNumber?: string }).mobileNumber ?? "",
      role: d.role,
      roles: d.roles && d.roles.length > 0 ? d.roles : [d.role],
      onboardingStep: (d as unknown as { onboardingStep?: string | null }).onboardingStep ?? null,
      wrapupStep: d.wrapupStep ?? null,
      buildPabStep: d.buildPabStep ?? null,
      buildExpertStep: d.buildExpertStep ?? null,
      createdAt: d.createdAt,
      codeOfConductAccepted: d.codeOfConductAccepted,
      activeWorkspace: d.activeWorkspace ?? null,
      // Absent only from an API that predates the flag, where everyone could apply.
      canApplyAsExpert: d.canApplyAsExpert ?? true,
      authProvider: d.authProvider ?? null,
      organizationCode: d.organizationCode ?? null,
      teamCode: d.teamCode ?? null,
    };
  };

  // DISABLED (multi-account): buildSlots() shaped the switcher's row list and
  // refreshConnectedAccounts() fetched the workspace group from
  // GET /users/me/connected-accounts (that endpoint is commented out on the
  // backend too). Restore both alongside the auth.ts blocks they depend on.
  //
  // /**
  //  * Build the AccountSlot[] for the UI.
  //  * Self is always at index 0. Others come from GET /users/me/connected-accounts (workspace group).
  //  * Other slots use hasStoredToken / cookies (fwd_token_<userId>) — no cookie means “Sign in” / add flow.
  //  */
  // const buildSlots = useCallback(
  //   (selfUser: User | null, connected: AccountMeta[]): AccountSlot[] => {
  //     if (!selfUser) return [];
  //     const selfSlot: AccountSlot = {
  //       index: 0,
  //       userId: selfUser.id,
  //       email: selfUser.email,
  //       fullName: selfUser.fullName,
  //       role: selfUser.role,
  //       roles: selfUser.roles,
  //       profilePictureUrl: null,
  //       hasToken: true,
  //       token: getAccessToken() ?? "",
  //       hasStoredToken: true,
  //     };
  //     const others: AccountSlot[] = connected
  //       .filter((c) => c.userId !== selfUser.id)
  //       .map((c, i) => ({
  //         index: i + 1,
  //         userId: c.userId,
  //         email: c.email,
  //         fullName: c.fullName,
  //         role: c.role,
  //         roles: c.roles,
  //         profilePictureUrl: c.profilePictureUrl,
  //         hasToken: false,
  //         token: "",
  //         hasStoredToken: hasUserToken(c.userId),
  //       }));
  //     return [selfSlot, ...others];
  //   },
  //   [],
  // );
  //
  // /**
  //  * Fetch connected accounts from the DB and update display state.
  //  * NEVER changes the session token or the active user in this tab.
  //  */
  // const refreshConnectedAccounts = useCallback(
  //   async (forUser?: User | null) => {
  //     try {
  //       const selfUser = forUser ?? user;
  //       if (!selfUser) return;
  //
  //       const { items } = await api.users.getConnectedAccounts();
  //
  //       const localMeta = getAccountsMeta();
  //       const merged: AccountMeta[] = [];
  //
  //       const selfMeta = localMeta.find((m) => m.userId === selfUser.id);
  //       merged.push(
  //         selfMeta ?? {
  //           userId: selfUser.id,
  //           email: selfUser.email,
  //           fullName: selfUser.fullName,
  //           role: selfUser.role,
  //           roles: selfUser.roles,
  //           profilePictureUrl: null,
  //         },
  //       );
  //
  //       for (const item of items) {
  //         if (item.userId === selfUser.id) continue;
  //         const local = localMeta.find((m) => m.userId === item.userId);
  //         merged.push({
  //           userId: item.userId,
  //           email: item.email || local?.email || "",
  //           fullName: item.fullName || local?.fullName || "",
  //           role: item.role || local?.role || "",
  //           roles: item.roles?.length ? item.roles : (local?.roles ?? [item.role || ""]),
  //           profilePictureUrl: item.profilePictureUrl ?? local?.profilePictureUrl ?? null,
  //         });
  //       }
  //
  //       setAccountsMeta(merged);
  //       setAccounts(buildSlots(selfUser, merged));
  //     } catch {
  //       // Non-fatal — UI falls back to whatever was already in state.
  //     }
  //   },
  //   [user, buildSlots],
  // );

  // ---------------------------------------------------------------------------
  // checkAuth — runs once on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const checkAuth = async () => {
      // Clear storage from pre-cookie builds so a stale sessionStorage token
      // can't be mistaken for a live session. Old sessions are intentionally
      // not migrated — those users sign in again. Must run before
      // isAuthenticated().
      clearLegacyTokenStorage();

      if (isAuthenticated()) {
        const epoch = sessionEpochRef.current;
        try {
          const userData = await fetchUserWithToken(getAccessToken()!);
          // Drop the result only if a DIFFERENT identity signed in while this
          // was in flight. /login-verify-token mounts this provider and
          // exchanges a one-time token in parallel, so on impersonation (where
          // the cookie already holds the admin's token) both chains race and
          // the loser's user would otherwise win.
          //
          // Compared by epoch, not by token string: the 401 interceptor
          // refreshes the access token in place, so the token this started with
          // is routinely not the token it ends with for the SAME user. Reading
          // that as a takeover discarded a valid user and rendered the app
          // signed-out on every load that happened to refresh.
          if (sessionEpochRef.current !== epoch) {
            setLoading(false);
            return;
          }
          setUser(userData);
          setAuthStatus(true);
        } catch (error) {
          const isHandledByInterceptor = (error as Record<string, unknown>)?.isAuthRedirect;
          if (!isHandledByInterceptor) {
            clearSessionToken();
            setUser(null);
            setAuthStatus(false);
          }
        }
      } else {
        setUser(null);
        setAuthStatus(false);
      }

      setLoading(false);
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The fwd_token cookie is shared by every tab, so a sign-out in one tab must
  // tear down the others: they still hold an authenticated React tree in memory
  // and would keep rendering as "logged in" until their next failed request.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
    ch.onmessage = (ev: MessageEvent<LogoutBroadcastMessage>) => {
      const data = ev.data;
      if (!data || typeof data !== "object") return;
      if (data.type !== "logout") return;

      // Nothing to tear down in a tab that was never signed in.
      if (!getAccessToken() && !user) return;

      clearSessionToken();
      setUser(null);
      setAuthStatus(false);
      logoutUser();
      showToast({ type: "warning", message: LOGGED_OUT });
      navigate("/login", { replace: true });
    };
    return () => ch.close();
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // getCurrentUser — used internally to refresh user state
  // ---------------------------------------------------------------------------

  const getCurrentUser = async () => {
    const userData = await api.users.getCurrentUser();
    if (userData) {
      const normalizedUser = {
        ...userData,
        roles: userData.roles && userData.roles.length > 0 ? userData.roles : [userData.role],
        // Absent only from an API that predates the flag, where everyone could apply.
        canApplyAsExpert: userData.canApplyAsExpert ?? true,
      };
      setUser(normalizedUser);
      setAuthStatus(true);

      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("user_id", normalizedUser.id);
      identifyEvent.set("email", normalizedUser.email);
      identifyEvent.set("name", normalizedUser.fullName);
      identifyEvent.set("role", normalizedUser.role);
      identifyEvent.set("roles", normalizedUser.roles);
      identifyUser(normalizedUser.email, identifyEvent);
    }
    return userData;
  };

  // DISABLED (multi-account): told other tabs to refresh their switcher after a
  // new account finished login/signup in the add-account tab.
  //
  // const broadcastAccountAdded = () => {
  //   if (typeof BroadcastChannel !== "undefined") {
  //     const ch = new BroadcastChannel("fwd_account_added");
  //     ch.postMessage({ type: "account_added" });
  //     ch.close();
  //   }
  // };

  // ---------------------------------------------------------------------------
  // Onboarding redirect helper
  // ---------------------------------------------------------------------------

  const getOnboardingRedirectRoute = async (userRole?: string, token?: string): Promise<string> => {
    try {
      const stepResponse = await api.users.getOnboardingStep(token);
      const redirectRoute = getRedirectRoute(stepResponse.step as OnboardingStep | null, userRole);
      return redirectRoute || "/home";
    } catch {
      return "/home";
    }
  };

  // ---------------------------------------------------------------------------
  // Login (password / magic-link direct)
  // ---------------------------------------------------------------------------

  const login = async (credentials: LoginCredentials) => {
    try {
      setLoading(true);
      const response = await publicApi.auth.login(credentials);

      const userData = await fetchUserWithToken(response.accessToken);

      sessionEpochRef.current += 1;
      setSessionToken(response.accessToken);

      setUser(userData);
      setAuthStatus(true);
      userLoggedIn({ user_id: userData.id, user_role: userData.role, login_method: "email" });

      const route = await getOnboardingRedirectRoute(userData.role);
      // A deep link they were sent to sign in from wins, once onboarding is done.
      const back = takeReturnPath();
      navigate(back && route === "/home" ? back : route);
    } catch (error) {
      showToast({ type: "error", message: LOGIN_FAILED });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Send magic-link login email
  // ---------------------------------------------------------------------------

  // connect_to_user_id / expected_target_user_id are no longer sent: they only
  // ever came from add-account mode, so the backend never links accounts now.
  const sendLoginLink = async (email: string) => {
    await publicApi.auth.sendLoginLink(email);
  };

  // ---------------------------------------------------------------------------
  // Verify magic-link token (login flow)
  // ---------------------------------------------------------------------------

  // Shared tail for any flow that yields a login response carrying an
  // accessToken (+ optional pendingProgramExternalId): magic-link verify and
  // review-account password login both funnel through here so session
  // activation, analytics and deep-link routing stay identical.
  const activateSessionFromLoginResponse = async (response: {
    accessToken: string;
    pendingProgramExternalId?: string | null;
  }): Promise<{ route: string }> => {
    const newToken = response.accessToken;

    let newUserData: User;
    try {
      newUserData = await fetchUserWithToken(newToken);
    } catch {
      clearSessionToken();
      setAuthStatus(false);
      throw new Error("Failed to retrieve user data after verification");
    }

    sessionEpochRef.current += 1;
    setSessionToken(newToken);
    setUser(newUserData);
    setAuthStatus(true);
    api.users.patchLastLogin().catch(() => {});

    userLoggedIn({ user_id: newUserData.id, user_role: newUserData.role, login_method: "email" });
    const route = await getOnboardingRedirectRoute(newUserData.role);

    const pendingProgramId =
      response.pendingProgramExternalId || localStorage.getItem("program_join_id") || null;
    const onboardingComplete = route === "/home";

    if (response.pendingProgramExternalId) {
      localStorage.setItem("program_join_id", response.pendingProgramExternalId);
    }

    // The page they were sent to sign in from (e.g. an event from an email)
    // comes first — it is what they just asked for. See postLoginRedirect.
    const back = takeReturnPath();
    if (back && onboardingComplete) return { route: back };

    return {
      route: pendingProgramId && onboardingComplete ? `/explore/${pendingProgramId}` : route,
    };
  };

  // Review-account password login. The backend accepts this ONLY for the
  // configured review email; any other email gets a 403 there.
  const passwordLogin = async (email: string, password: string): Promise<{ route: string }> => {
    const response = await publicApi.auth.passwordLogin(email, password);
    return activateSessionFromLoginResponse(response);
  };

  const verifyLoginToken = async (token: string): Promise<{ route: string }> => {
    // 1. Exchange the one-time token for a JWT.
    const response = await publicApi.auth.verifyLoginToken(token);
    const newToken = response.accessToken;

    // 2. Fetch the newly-authenticated user's data with the new token.
    let newUserData: User;
    try {
      newUserData = await fetchUserWithToken(newToken);
    } catch {
      clearSessionToken();
      setAuthStatus(false);
      throw new Error("Failed to retrieve user data after verification");
    }

    // 3. Activate the session as the new user.
    sessionEpochRef.current += 1;
    setSessionToken(newToken);
    setUser(newUserData);
    setAuthStatus(true);
    api.users.patchLastLogin().catch(() => {});

    userLoggedIn({ user_id: newUserData.id, user_role: newUserData.role, login_method: "email" });
    const route = await getOnboardingRedirectRoute(newUserData.role);

    // Deep link: send an invited achiever to the program they were invited to
    // rather than their default landing route.
    //
    // SERVER FIRST, localStorage second. The server value comes from an open
    // (INVITED) program_invitations row keyed on this user's email, so it
    // works in any browser or on any device -- including the case that used
    // to break: start signup on one browser, finish on another, and the
    // localStorage copy is simply gone. localStorage remains the fallback
    // because it is the ONLY record for someone who arrived from a shared
    // public link (no invitation row exists for them).
    //
    // Costs no extra request: pendingProgramExternalId rides the existing
    // login-verify response.
    //
    // ONBOARDING WINS. The deep link only applies once the user has actually
    // finished onboarding. getOnboardingRedirectRoute returns "/home" exactly
    // when onboarding is complete, so anything else means they still owe a
    // step (e.g. /signup/3) and must go there first -- jumping them to a
    // program page would skip the rest of their signup, and OnboardingGuard
    // would drag them back anyway the moment they touched a guarded route.
    // The invitation is not lost: it lives in program_invitations, so it
    // resolves again on their next login, and the signup steps carry
    // program_join_id through to their own completion handler.
    const pendingProgramId =
      response.pendingProgramExternalId || localStorage.getItem("program_join_id") || null;
    const onboardingComplete = route === "/home";

    // Re-seed localStorage from the server value so the deep link survives the
    // REST of onboarding. Without this, a user who signed up on one browser
    // and resumes on another reaches /signup/3, finishes, and lands on a
    // generic /explore -- AchieverGrowthForm reads program_join_id at the end
    // of onboarding and this browser has never had it written.
    if (response.pendingProgramExternalId) {
      localStorage.setItem("program_join_id", response.pendingProgramExternalId);
    }

    // The page they were sent to sign in from (e.g. an event from an email)
    // comes first — it is what they just asked for. See postLoginRedirect.
    const back = takeReturnPath();
    if (back && onboardingComplete) return { route: back };

    return {
      route: pendingProgramId && onboardingComplete ? `/explore/${pendingProgramId}` : route,
    };
  };

  // ---------------------------------------------------------------------------
  // Verify signup email token
  // ---------------------------------------------------------------------------

  const verifyEmail = async (token: string) => {
    const response = await publicApi.auth.verifyEmail(token);

    // Deep link, server-first. If the server says this user has an open
    // achiever invitation, write it into the same key the signup steps
    // already read so the rest of the flow needs no new plumbing -- and so a
    // user who started signup in a different browser (empty localStorage)
    // still reaches the program they were invited to.
    //
    // Only ever WRITES when the server has a value; it never clears an
    // existing local one, because a shared-public-link signup has no
    // invitation row and localStorage is that user's only record.
    if (response.pendingProgramExternalId) {
      localStorage.setItem("program_join_id", response.pendingProgramExternalId);
    }
    try {
      const userData = await fetchUserWithToken(response.accessToken);

      sessionEpochRef.current += 1;
      setSessionToken(response.accessToken);
      setUser(userData);
      setAuthStatus(true);
    } catch (error) {
      console.error("Failed to get user after email verification:", error);
      clearSessionToken();
      setAuthStatus(false);
      throw new Error("Failed to retrieve user data after email verification");
    }
  };

  // ---------------------------------------------------------------------------
  // DISABLED (multi-account): switchAccount / addAccount / removeAccount
  // ---------------------------------------------------------------------------
  //
  // switchAccount() read another user's JWT out of fwd_token_<userId> and made
  // it this tab's session; addAccount() opened /login in a new tab carrying an
  // add-account nonce. Both are meaningless with one shared session cookie.
  // Restore them together with the auth.ts helpers and the backend endpoints.
  //
  // const switchAccount = async (userId: string) => {
  //   const token = getUserToken(userId);
  //   if (!token) {
  //     addAccount();
  //     return;
  //   }
  //
  //   await queryClient.cancelQueries();
  //
  //   let userData: User;
  //   try {
  //     userData = await fetchUserWithToken(token);
  //   } catch {
  //     removeUserToken(userId);
  //     showToast({ type: "warning", message: ACCOUNT_SWITCH_FAILED });
  //     addAccount();
  //     return;
  //   }
  //
  //   upsertAccountMeta({
  //     userId: userData.id,
  //     email: userData.email,
  //     fullName: userData.fullName,
  //     role: userData.role,
  //     roles: userData.roles,
  //     profilePictureUrl: null,
  //   });
  //   storeUserToken(userId, token); // refresh cookie TTL
  //   setSessionToken(token);
  //   window.location.href = "/home";
  // };
  //
  // const addAccount = (expectedTargetUserId?: string) => {
  //   if (!user?.id) {
  //     window.open("/login", "_blank");
  //     return;
  //   }
  //   setAddAccountExpectedTargetUserId(expectedTargetUserId);
  //   const nonce = createAddAccountIntent(user.id);
  //   window.open("/login", `fwd_add_account:${nonce}`);
  // };
  //
  // const removeAccount = (userId: string) => { ... }  // superseded by logout()

  // ---------------------------------------------------------------------------
  // Sign out
  // ---------------------------------------------------------------------------

  /**
   * Clear the shared session cookie and tell every other tab to do the same.
   * With one cookie there is exactly one session, so this is a full sign-out —
   * what logout() and logoutAll() used to do separately.
   */
  const logout = () => {
    localStorage.removeItem("program_join_id");
    localStorage.removeItem("program_join_role");
    if (user) {
      userLoggedOut({ user_id: user.id, user_role: user.role, logout_method: "manual" });
    }
    broadcastLogout();
    clearAllAuth();
    setUser(null);
    setAuthStatus(false);
    logoutUser();
    navigate("/login");
  };

  // ---------------------------------------------------------------------------
  // refreshUser
  // ---------------------------------------------------------------------------

  const refreshUser = async () => {
    try {
      await getCurrentUser();
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  };

  // ---------------------------------------------------------------------------
  // Impersonation
  // ---------------------------------------------------------------------------

  const impersonate = async (email: string) => {
    const { loginUrl } = await getImpersonateLoginUrl(email);
    window.location.href = loginUrl;
  };

  const getImpersonateLoginUrl = async (
    email: string,
    secretKey?: string,
  ): Promise<{ loginUrl: string; email: string }> => {
    return api.auth.impersonate({ email, secretKey });
  };

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    verifyEmail,
    sendLoginLink,
    verifyLoginToken,
    passwordLogin,
    isAuthenticated: authStatus,
    refreshUser,
    impersonate,
    getImpersonateLoginUrl,
    // DISABLED (multi-account):
    // accounts,
    // activeAccountIndex,
    // logoutAll,
    // refreshConnectedAccounts,
    // switchAccount,
    // addAccount,
    // removeAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthProvider };
